import { useCallback, useMemo } from "react";
import { useDispatch } from "react-redux";
import { TInspectionTask, TInspectionTaskLink } from "../../types/types";
import { getInspectionTaskColumns, getInspectionTaskLinksColumns } from "../../Pages/InspectionsDetail/columns";
import { useInspectionData, useInspectionTaskCRUD, useInspectionTaskLinkCRUD, useInspectionTable } from "./inspections";
import { setModal } from "../../store/Utilities";

export type UseInspectionsOptions = {
    selectedModelNumber: string | null;
    inspectionSearchTerm: string;
    linkSearchTerm: string;
    currentActiveInspectionTask: { original: TInspectionTask } | null;
    setCurrentActiveInspectionTask: React.Dispatch<React.SetStateAction<{ original: TInspectionTask } | null>>;
    onEditTask?: (task: TInspectionTask) => void;
    onEditTaskLink?: (link: TInspectionTaskLink) => void;
};

/**
 * Main hook for inspection management
 * Composes smaller hooks for data, CRUD operations, and table management
 */
export function useInspections({
    selectedModelNumber,
    inspectionSearchTerm,
    linkSearchTerm,
    currentActiveInspectionTask,
    setCurrentActiveInspectionTask,
    onEditTask: onEditTaskClick,
    onEditTaskLink: onEditTaskLinkClick
}: UseInspectionsOptions) {
    // Data layer - fetching and state management
    const {
        isLoading,
        injectedModelData,
        currentActiveModel,
        activeInspectionId,
        newlyCreatedInspection,
        addInspectionToModel,
        clearNewlyCreatedInspection,
        inspectionTaskLinks
    } = useInspectionData(selectedModelNumber);

    // Get active task ID for link CRUD operations
    const activeInspectionTaskId = currentActiveInspectionTask?.original?.inspectionTaskId || null;

    // CRUD operations for tasks
    const { addTask, editTask, removeTask } = useInspectionTaskCRUD(activeInspectionId);

    // CRUD operations for task links
    const { addTaskLink, editTaskLink, removeTaskLink } = useInspectionTaskLinkCRUD(activeInspectionTaskId);

    const dispatch = useDispatch();

    // Action handlers for task table columns
    const onEditTask = useCallback((task: TInspectionTask) => {
        onEditTaskClick?.(task);
    }, [onEditTaskClick]);

    const onRemoveTask = useCallback((task: TInspectionTask) => {
        dispatch(setModal({
            open: true,
            Chilren: () => (
                <div>
                    <h3 className="text-lg font-semibold mb-2">Delete Task</h3>
                    <p>Are you sure you want to delete task "{task.taskHeader}"? </p>
                    <p>This will also delete all associated links.</p>
                </div>
            ),
            onConfirm: () => { removeTask(task); setCurrentActiveInspectionTask(null) },
        }));
    }, [dispatch, removeTask, setCurrentActiveInspectionTask]);

    // Action handlers for link table columns
    const onEditLink = useCallback((link: TInspectionTaskLink) => {
        onEditTaskLinkClick?.(link);
    }, [onEditTaskLinkClick]);

    const onRemoveLink = useCallback((link: TInspectionTaskLink) => {
        dispatch(setModal({
            open: true,
            Chilren: () => (
                <div>
                    <h3 className="text-lg font-semibold mb-2">Delete Link</h3>
                    <p>Are you sure you want to delete link "{link.linkText}"?</p>
                </div>
            ),
            onConfirm: () => removeTaskLink(link),
        }));
    }, [dispatch, removeTaskLink]);

    // Table columns with actions
    const taskColumns = useMemo(
        () => getInspectionTaskColumns({ onEdit: onEditTask, onRemove: onRemoveTask }),
        [onEditTask, onRemoveTask]
    );

    const linkColumns = useMemo(
        () => getInspectionTaskLinksColumns({ onEdit: onEditLink, onRemove: onRemoveLink }),
        [onEditLink, onRemoveLink]
    );

    // Current tasks data from active model
    const currentTasksData = useMemo(
        () => currentActiveModel?.inspectionTasks.sort((a, b) => a.listOrder - b.listOrder) || [],
        [currentActiveModel]
    );

    // Current task links data - look up fresh from Redux by task ID
    const currentTaskLinks = useMemo<TInspectionTaskLink[]>(
        () => {
            if (!activeInspectionTaskId) return [];
            return inspectionTaskLinks.filter(
                link => Number(link.inspectionTaskId) === Number(activeInspectionTaskId)
            );
        },
        [inspectionTaskLinks, activeInspectionTaskId]
    );

    // Table management
    const {
        inspectionTaskTableData,
        taskPagination,
        inspectionTaskLinksData,
        linkPagination
    } = useInspectionTable({
        tasks: currentTasksData,
        taskLinks: currentTaskLinks,
        taskColumns,
        linkColumns,
        taskSearchTerm: inspectionSearchTerm,
        linkSearchTerm,
        currentActiveInspectionTask,
        setCurrentActiveInspectionTask
    });

    return {
        // Table data
        inspectionTaskTableData,
        inspectionPagination: taskPagination,
        inspectionTaskLinksData,
        inspectionTaskLinksPagination: linkPagination,

        // Model data
        injectedModelData,
        currentActiveModel,

        // Task CRUD operations
        addTask,
        editTask,
        onClickAddInspectionToModel: addInspectionToModel,

        // Task Link CRUD operations
        addTaskLink,
        editTaskLink,

        // State
        isLoading,
        newlyCreatedInspection,
        clearNewlyCreatedInspection,

        // Active task ID for link operations
        activeInspectionTaskId,
    };
}